import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from './supabase-data/LoadingAndError';
import { useIsMobile } from '@/hooks/use-mobile';
import { FontSizeOption } from '@/types/display';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import DisplayCard from '@/components/shared/DisplayCard';

interface DailyStudyContent {
  selectedItems?: string[];
  fontSize?: FontSizeOption;
}

interface DailyStudyPageProps {
  content: DailyStudyContent;
  isHomePage?: boolean;
}

interface StudyItem {
  id: string;
  type: string;
  lesson: string;
  link?: string;
}

interface StudyData {
  title: string;
  description: string;
  hebrew_date?: string;
  gregorian_date?: string;
  fallback_mode?: boolean;
  items: StudyItem[];
}

const DailyStudyPage: React.FC<DailyStudyPageProps> = ({
  content = {},
  isHomePage
}) => {
  const isMobile = useIsMobile();
  const [studyData, setStudyData] = useState<StudyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        // Force fresh data by calling scraper directly with cache busting parameters
        const cacheBuster = new URLSearchParams();
        cacheBuster.set('t', Date.now().toString());
        cacheBuster.set('clear_cache', 'true');
        cacheBuster.set('force', 'true');
        cacheBuster.set('bypass_cache', 'true');
        
        const scraperUrl = `https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/scrape-daily-study?${cacheBuster.toString()}`;
        
        console.log('Fetching completely fresh data from scraper with URL:', scraperUrl);
        
        const response = await fetch(scraperUrl, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind3b3NmaGF0a3pxc2d6aGtpZ3B4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzI5MDcyNDMsImV4cCI6MjA0ODQ4MzI0M30.YR_7x4OpZpqRfxT3pEvVa9UF3TJ-p3q7Ioe9Xpr-MdM'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Check if response contains an error
        if (data.error) {
          throw new Error(data.error);
        }

        console.log('Fresh daily study data received from scraper with updated Rambam mapping:', data);
        setStudyData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching daily study data:', err);
        setError(err instanceof Error ? err.message : 'שגיאה בטעינת הלימוד היומי');
      } finally {
        setLoading(false);
      }
    };

    fetchStudyData();

    // Set up periodic refresh every 2 minutes to get latest data
    const interval = setInterval(fetchStudyData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter items based on selected items
  const filteredItems = studyData?.items?.filter((item: StudyItem) => !content?.selectedItems || content.selectedItems.length === 0 || content.selectedItems.includes(item.type)) || [];
  
  if (loading) {
    return <LoadingSpinner isLoading={loading} />;
  }
  
  if (error) {
    return <div className="max-w-full px-4">
        <Alert className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>שגיאה בטעינת הלימוד היומי</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{error}</p>
            <p className="text-sm text-muted-foreground">
              אנא נסה שוב או פנה למנהל המערכת
            </p>
          </AlertDescription>
        </Alert>
      </div>;
  }
  
  if (!studyData) {
    return null;
  }
  
  const itemsToShow = filteredItems.length > 0 ? filteredItems : studyData.items;
  
  return <div className="max-w-full mx-auto px-4 py-4 max-h-screen overflow-hidden">
      {/* Show fallback mode warning if applicable */}
      {studyData.fallback_mode && <Alert className="mb-6 border-orange-200 bg-orange-50 rounded-2xl">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800">מצב חירום</AlertTitle>
          <AlertDescription className="text-orange-700">
            המערכת פועלת במצב חירום עם נתונים זמניים. חלק מהתוכן עשוי להיות לא מעודכן.
          </AlertDescription>
        </Alert>}

      {/* Responsive grid ensuring all cards fit on screen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-h-[calc(100vh-200px)] overflow-hidden">
        {itemsToShow.map((item: StudyItem) => <DisplayCard key={item.id} title={item.type} time={item.lesson} dividerPosition="afterTitle" additionalContent={item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm lg:text-base transition-colors">
                  <span>קישור לשיעור</span>
                  <ExternalLink className="w-4 h-4 mr-1" />
                </a>} />)}
      </div>
      
      {itemsToShow.length === 0 && <div className="flex items-center justify-center p-8">
          <p className="text-gray-500 text-lg">אין שיעורים זמינים</p>
        </div>}
    </div>;
};

export default DailyStudyPage;
